import React, { useRef, useState, memo, useEffect } from 'react';
import {
  Modal,
  Collapse,
  Tooltip,
  Row,
  Col,
  Input,
  Button,
  Select,
  Form,
  Spin,
  Typography,
  Menu,
  Dropdown,
} from 'antd';
import { SPQLResultTable } from '../../../shared-components/SPQLResultTable';
import styles from './spql-container.module.sass';
import { Tabs } from 'antd';
import styled from 'styled-components';
import { ReactComponent as spql_tab_icon } from '../../../assets/icons/spql_icon.svg';
import { ReactComponent as down_icon } from '../../../assets/down_arrow.svg';
import { ReactComponent as right_icon } from '../../../assets/Right.svg';
import { ReactComponent as description_icon } from '../../../assets/icons/notes.svg';
import { ReactComponent as copy_icon } from '../../../assets/icons/Copy.svg';
import { ReactComponent as play_icon } from '../../../assets/icons/PlayCircleHeader.svg';
import { ReactComponent as edit_icon } from '../../../assets/icons/Edit.svg';
import { ReactComponent as delete_icon } from '../../../assets/icons/Delete.svg';
import { ReactComponent as shared_icon } from '../../../assets/icons/SharedIcon.svg';
import { ReactComponent as no_data_icon } from '../../../assets/icons/no_results.svg';
import Icon, { UserOutlined, MoreOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { SPQLEditor } from '../../../shared-components/SPQLEditor';
import {
  getQueryExamples,
  getQueryExamplesLoadingStatus,
  getSPQLCreateQueryStatus,
  getSPQLFieldsMetadata,
  getSPQLLoading,
  getSPQLQuery,
  getSPQLQueryValidationMessage,
  getSPQLRecentQueries,
  getSPQLRecentQueriesLoadingStatus,
  getSPQLSavedQueries,
  getSPQLSorting,
} from '../../../redux/selectors/deep_scan';
import {
  infoNotification,
  showNotification,
  simpleErrorNotification,
} from '../../../shared-components/Notifications';
import TextArea from 'antd/lib/input/TextArea';
import { useForm } from 'antd/lib/form/Form';
import {
  useHistory,
  useLocation,
} from 'react-router-dom/cjs/react-router-dom.min';
import { STATUS } from '../../../redux/constants';
import LoadingSilentPush from '../../../shared-components/LoadingSilentPush';
import PermissionDeniedPage from '../../../shared-components/PermissionDeniedPage';
import { changeTitle } from '../../../redux/actions/app';
import {
  changeSPQLQueryValidationMessage,
  resetDeepScanToInitialState,
  setCreateSPQLQueryStatus,
} from '../../../redux/actions/deep_scan';
import DeleteRecipeModal from '../../../shared-components/DeleteRecipeModal';
import { incrementUIRequestsCounter } from '../../../redux/utils/websocket/incrementUIRequestsCounter';

const { Panel } = Collapse;
const { Title, Text } = Typography;

const MAX_SORTING_FIELDS = 5;

function SPQLBadge(props) {
  return (
    <div className={styles['spql-chip']}>
      <span className={styles['spql-chip-text']}>Web Scanner</span>
    </div>
  );
}

const SPQLTabPane = styled.div`
  .ant-tabs-nav {
    padding-left: 16px;
    background: #1c3138;
  }
  .ant-tabs-tab {
    padding: 8px;
  }
  .ant-tabs-tab:not(:first-child) {
    margin-left: 16px;
  }
  .ant-tabs-tabpane {
    background: #0b171b;
  }
`;

const SPQLTabPaneLabel = memo(function SPQLTabPaneLabel({ label, ...props }) {
  const SPQLTabIcon = (props) => <Icon component={spql_tab_icon} {...props} />;
  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <SPQLTabIcon style={{ fontSize: 16, marginRight: 10 }} />
      <span>{label}</span>
    </div>
  );
});

const SPQLCollapse = styled.div`
  .ant-collapse-header {
    padding: 0 !important;
  }
  .ant-collapse-content-box {
    padding: 0;
  }
`;

function SPQLQueryExample(props) {
  const CopyIcon = (props) => <Icon component={copy_icon} {...props} />;
  const handleCopy = () => {
    navigator.clipboard.writeText(props.query);
    showNotification(
      infoNotification({
        statusCode: 100,
        message: 'Copied to clipboard',
      }),
    );
  };
  return (
    <div className={styles['spql-query-example-container']} style={props.style}>
      <Button
        onClick={() => handleCopy()}
        ghost
        style={{
          border: 0,
          color: 'white',
          marginRight: 4,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
        icon={<CopyIcon style={{ fontSize: 16 }} />}
      />
      <span style={{ width: '100%' }}>{props.query}</span>
    </div>
  );
}

function SPQLQueryExamples(props) {
  const queryExamples = useSelector(getQueryExamples);
  const queryExamplesLoadingStatus = useSelector(getQueryExamplesLoadingStatus);
  return (
    <div>
      <Row gutter={16}>
        {queryExamplesLoadingStatus === 'loading' && (
          <div
            style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
          >
            <Spin />
          </div>
        )}
        {queryExamples &&
          queryExamples.map((query, idx) => (
            <Col key={idx} lg={24} xxl={12}>
              <Tooltip title={query['name']}>
                <div>
                  <SPQLQueryExample
                    key={`${query['query']}-${idx}`}
                    query={query['query']}
                  />
                </div>
              </Tooltip>
            </Col>
          ))}
        {queryExamples?.length === 0 &&
          queryExamplesLoadingStatus !== 'loading' && (
            <div
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <span style={{ color: 'white' }}>No query examples</span>
            </div>
          )}
      </Row>
    </div>
  );
}

function getSortOptions(sortableFields) {
  if (!sortableFields || sortableFields.length === 0) return [];
  const ascOptions = sortableFields.map((field) => {
    return {
      label: field + '/asc',
      value: field + '/asc',
    };
  });
  const descOptions = sortableFields.map((field) => {
    return {
      label: field + '/desc',
      value: field + '/desc',
    };
  });
  let selectOptions = [...ascOptions, ...descOptions];
  selectOptions.sort((a, b) => (a.value > b.value ? 1 : -1));

  return selectOptions;
}

const StyledSPQLEditor = styled.div`
  .monaco-scrollable-element.editor-scrollable {
    left: 8px !important;
  }
`;

function SPQLRawQuery({
  changeSPQLQuery,
  changeSPQLSorting,
  sortableFields,
  getDeepScan,
  ...props
}) {
  const PlayIcon = (props) => <Icon component={play_icon} {...props} />;
  const spqlQuery = useSelector(getSPQLQuery);
  const spqlSorting = useSelector(getSPQLSorting);
  const fieldsMetadata = useSelector(getSPQLFieldsMetadata);
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const spqlQueryValidationMessage = useSelector(getSPQLQueryValidationMessage);
  const savedQueries = useSelector(getSPQLSavedQueries);

  const urlParams = new URLSearchParams(location.search);
  const queryUUID = urlParams.get('query-uuid');
  const urlSorting = urlParams.get('sorting')?.split(',') || [];

  const handleReset = () => {
    dispatch(changeSPQLQueryValidationMessage(''));
    if (queryUUID) {
      const savedQuery = savedQueries.find((query) => query.uuid === queryUUID);
      if (savedQuery) {
        changeSPQLQuery(savedQuery.query);
        changeSPQLSorting(['scan_date/desc']);
        return;
      }
    }
    changeSPQLQuery('');
    changeSPQLSorting(['scan_date/desc']);
  };

  const updateURL = (query, sorting) => {
    const params = new URLSearchParams(location.search);
    const queryUUID = params.get('query-uuid');
    if (queryUUID) {
      return;
    }

    history.push(
      `/web-scanner?query=${encodeURIComponent(
        query,
      )}&sorting=${sorting.map((item) => encodeURIComponent(item)).join(',')}`,
    );
  };

  let selectOptions = getSortOptions(sortableFields);

  return (
    <div style={{ paddingTop: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {fieldsMetadata && Object.keys(fieldsMetadata).length > 0 && (
          <StyledSPQLEditor
            style={{
              flexGrow: 1,
              width: 'calc(100% - 42px)',
              height: 'fit-content',
            }}
          >
            <SPQLEditor
              data-cy={'spqlRawQueryEditor'}
              currentSorting={urlSorting}
              onChange={(val) => {
                changeSPQLQuery(val);
              }}
              submit={(val, currentSorting) => {
                if (!isSPQLQueryValid(val, fieldsMetadata)) {
                  if (val.trim().length === 0) {
                    dispatch(
                      changeSPQLQueryValidationMessage(
                        'Please enter a valid query',
                      ),
                    );
                    return;
                  }
                  dispatch(
                    changeSPQLQueryValidationMessage(
                      'The Field Key does not exist, or has been entered incorrectly.',
                    ),
                  );
                  return;
                }
                getDeepScan({
                  query: val,
                  sort: spqlSorting,
                  limit: 100,
                  skip: 0,
                });
                updateURL(val, spqlSorting);
              }}
              value={spqlQuery}
              options={{
                contextmenu: false,
                fontSize: 18,
                minimap: { enabled: false },
                lineNumbers: 'off',
                scrollBeyondLastLine: false,
                automaticLayout: true,
                scrollbar: {
                  vertical: 'hidden',
                  horizontalScrollbarSize: 4,
                },
                overviewRulerLanes: 0,
              }}
              fieldsMetadata={fieldsMetadata}
              height={42}
            />
          </StyledSPQLEditor>
        )}
        <Button
          onClick={() => {
            if (!isSPQLQueryValid(spqlQuery, fieldsMetadata)) {
              if (spqlQuery.trim().length === 0) {
                dispatch(
                  changeSPQLQueryValidationMessage(
                    'Please enter a valid query',
                  ),
                );
                return;
              }
              dispatch(
                changeSPQLQueryValidationMessage(
                  'The Field Key does not exist, or has been entered incorrectly.',
                ),
              );
              return;
            }
            getDeepScan({
              query: spqlQuery,
              sort: spqlSorting,
              limit: 100,
              skip: 0,
            });
            updateURL(spqlQuery, spqlSorting);
          }}
          style={{
            background: '#177DDC',
            padding: 0,
            height: 42,
            minWidth: 42,
            borderRadius: '0 4px 4px 0',
          }}
          icon={<PlayIcon style={{ fontSize: 24 }} />}
        />
      </div>
      {spqlQueryValidationMessage?.length > 0 && (
        <div style={{ marginLeft: 8, marginTop: 2 }}>
          <span style={{ color: 'red' }}>{spqlQueryValidationMessage}</span>
        </div>
      )}
      <div className={styles['spql-raw-query-text']} style={{ marginTop: 16 }}>
        Sort order
      </div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Select
          data-cy={'spqlSelectSorting'}
          className={styles['spql-select-sorting']}
          mode="multiple"
          allowClear
          value={spqlSorting}
          options={selectOptions}
          onChange={(value) => {
            if (value.length > MAX_SORTING_FIELDS) {
              showNotification(
                simpleErrorNotification({
                  message: `You can only select up to ${MAX_SORTING_FIELDS} fields to sort by`,
                }),
              );
              return;
            }
            changeSPQLSorting(value);
            updateURL(spqlQuery, value);
          }}
        />
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
        <Tooltip
          title={
            !!queryUUID
              ? 'Reset the query to the previous saved state'
              : 'Reset the query to the empty state'
          }
        >
          <Button
            onClick={handleReset}
            ghost
            style={{ border: 0, marginTop: 8, padding: 0 }}
          >
            Reset
          </Button>
        </Tooltip>
      </div>
    </div>
  );
}

function SPQLRecentlyRanQueries({ ...props }) {
  const recentQueries = useSelector(getSPQLRecentQueries);
  const recentQueriesLoading = useSelector(getSPQLRecentQueriesLoadingStatus);

  return (
    <Row gutter={16}>
      {recentQueriesLoading === 'loading' && (
        <div
          style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
        >
          <Spin />
        </div>
      )}
      {recentQueries &&
        recentQueries.map((query, idx) => (
          <Col key={idx} lg={24} xxl={12}>
            <SPQLQueryExample
              key={`${query['query']}-${idx}`}
              query={query['query']}
            />
          </Col>
        ))}
      {recentQueries?.length === 0 && (
        <div
          style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
        >
          <span style={{ color: 'white' }}>No recently ran queries</span>
        </div>
      )}
    </Row>
  );
}

function SPQLQueries(props) {
  const [queryExampleCollapseIcon, setQueryExampleCollapseIcon] = useState(
    right_icon,
  );
  const [recentQueryCollapseIcon, setRecentQueryCollapseIcon] = useState(
    right_icon,
  );

  const PanelHeader = ({ text, collapseIcon, ...props }) => {
    const CollapseIcon = (props) => (
      <Icon component={collapseIcon} {...props} />
    );
    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <span className={styles['spql-collapse-header-text']}>{text}</span>
        <CollapseIcon
          style={{
            color: 'white',
            display: 'flex',
            padding: 8,
            fontSize: 12,
            justifyContent: 'flex-end',
          }}
        />
      </div>
    );
  };
  return (
    <>
      <SPQLCollapse style={{ marginTop: 16 }}>
        <Collapse
          destroyInactivePanel={true}
          onChange={(keys) => {
            if (keys.length > 0) {
              if (keys.includes('spql-query-examples')) {
                setQueryExampleCollapseIcon(right_icon);
              }
              if (keys.includes('spql-recently-run-queries')) {
                setRecentQueryCollapseIcon(right_icon);
              }
              if (!keys.includes('spql-query-examples')) {
                setQueryExampleCollapseIcon(down_icon);
              }
              if (!keys.includes('spql-recently-run-queries')) {
                setRecentQueryCollapseIcon(down_icon);
              }
              return;
            }
            setQueryExampleCollapseIcon(down_icon);
            setRecentQueryCollapseIcon(down_icon);
          }}
        >
          <Panel
            showArrow={false}
            header={
              <PanelHeader
                collapseIcon={queryExampleCollapseIcon}
                text={'Query examples'}
              />
            }
            style={{ textAlign: 'left' }}
            key="spql-query-examples"
          >
            <SPQLQueryExamples />
          </Panel>
          <Panel
            showArrow={false}
            header={
              <PanelHeader
                collapseIcon={recentQueryCollapseIcon}
                text={'Recent queries'}
              />
            }
            style={{ marginTop: 16, textAlign: 'left' }}
            key="spql-recently-run-queries"
          >
            <SPQLRecentlyRanQueries />
          </Panel>
        </Collapse>
      </SPQLCollapse>
    </>
  );
}

function SPQLRawQueryContainer({
  changeSPQLQuery,
  changeSPQLSorting,
  getDeepScan,
  sortableFields,
  ...props
}) {
  const spqlQueryLoadingStatus = useSelector(getSPQLLoading);
  const location = useLocation();
  const [activeKey, setActiveKey] = useState(
    spqlQueryLoadingStatus === 'success' ? [] : ['spql-query-examples'],
  );
  const [collapseIcon, setCollapseIcon] = useState(right_icon);
  const DownIcon = (props) => <Icon component={collapseIcon} {...props} />;
  const EditIcon = (props) => <Icon component={edit_icon} {...props} />;
  const SPQLHeaderText = (props) => (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {activeKey.length > 0 && (
        <>
          <span className={styles['spql-collapse-header-text']}>Query</span>
          <DownIcon
            style={{
              flexGrow: 1,
              display: 'flex',
              padding: 8,
              fontSize: 16,
              justifyContent: 'flex-end',
            }}
          />
        </>
      )}
      {activeKey.length === 0 && (
        <div
          style={{
            display: 'flex',
            flexGrow: 1,
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Button
            style={{
              fontWeight: 'bold',
              borderRadius: 4,
              border: 0,
              color: 'white',
              background: '#E8B339',
            }}
            onClick={() => {
              setActiveKey(['spql-query-examples']);
              setCollapseIcon(right_icon);
            }}
          >
            Edit Web Scanner Search Form
          </Button>
        </div>
      )}
    </div>
  );

  useEffect(() => {
    if (spqlQueryLoadingStatus === 'loading') {
      setActiveKey([]);
      setCollapseIcon(down_icon);
      return;
    }
    if (location.search?.trim().length === 0) {
      setActiveKey(['spql-query-examples']);
    }
  }, [spqlQueryLoadingStatus, location.search]);

  return (
    <div style={{ padding: '9px 16px 16px 16px' }}>
      <SPQLCollapse>
        <Collapse
          onChange={(keys) => {
            if (keys.length > 0) {
              if (keys[0] === 'spql-query-examples') {
                setActiveKey(['spql-query-examples']);
                setCollapseIcon(right_icon);
                return;
              }
            }
            setActiveKey([]);
            setCollapseIcon(down_icon);
          }}
          activeKey={activeKey}
        >
          <Panel
            style={{ textAlign: 'left' }}
            showArrow={false}
            collapsible={activeKey.length === 0 ? 'disabled' : 'icon'}
            key="spql-query-examples"
            header={<SPQLHeaderText />}
          >
            <SPQLRawQuery
              changeSPQLSorting={changeSPQLSorting}
              changeSPQLQuery={changeSPQLQuery}
              getDeepScan={getDeepScan}
              sortableFields={sortableFields}
            />
            <SPQLQueries />
          </Panel>
        </Collapse>
      </SPQLCollapse>
    </div>
  );
}

function SPQLSearchResults({
  getDeepScan,
  changeSPQLQuery,
  changeSPQLSelectedColumns,
  columns,
  resultsMetadata,
  data,
  ...props
}) {
  const [collapseIcon, setCollapseIcon] = useState(down_icon);
  const DownIcon = (props) => (
    <Icon style={{ color: 'whitel' }} component={collapseIcon} {...props} />
  );
  const NoDataIcon = (props) => <Icon component={no_data_icon} {...props} />;
  const SPQLHeaderText = (props) => (
    <>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <span className={styles['spql-results-header-text']}>Results</span>
        <DownIcon
          style={{
            color: 'white',
            padding: 8,
            fontSize: 16,
            justifyContent: 'flex-end',
          }}
        />
      </div>
      <div
        style={{
          borderTop: '1px solid rgba(255, 255, 255, 0.20)',
          marginTop: 8,
        }}
      />
    </>
  );

  const spqlQueryLoadingStatus = useSelector(getSPQLLoading);
  const [activeKey, setActiveKey] = useState(
    spqlQueryLoadingStatus === 'success' ? ['spql-results-table-section'] : [],
  );

  useEffect(() => {
    if (spqlQueryLoadingStatus === 'loading') {
      setActiveKey(['spql-results-table-section']);
      setCollapseIcon(right_icon);
      return;
    } else if (spqlQueryLoadingStatus === 'idle') {
      setActiveKey([]);
      setCollapseIcon(down_icon);
    }
  }, [spqlQueryLoadingStatus]);

  return (
    <div style={{ padding: '9px 16px 16px 16px' }}>
      <SPQLCollapse>
        <Collapse
          activeKey={activeKey}
          onChange={(keys) => {
            if (keys.length > 0) {
              if (keys[0] === 'spql-results-table-section') {
                setCollapseIcon(right_icon);
                setActiveKey(['spql-results-table-section']);
                return;
              }
            }
            setActiveKey([]);
            setCollapseIcon(down_icon);
          }}
        >
          <Panel
            style={{ textAlign: 'left' }}
            showArrow={false}
            key="spql-results-table-section"
            header={<SPQLHeaderText />}
          >
            {spqlQueryLoadingStatus === 'idle' && (
              <div className={styles.empty_state}>
                <NoDataIcon style={{ fontSize: 32, marginTop: '8px' }} />
                <Title level={4}>Query didn't run</Title>
                <Text>
                  For the results you need to fill out the query form and click
                  Search.
                </Text>
              </div>
            )}
            <SPQLResultTable
              getDeepScan={getDeepScan}
              changeSPQLQuery={changeSPQLQuery}
              changeSPQLSelectedColumns={changeSPQLSelectedColumns}
              resultsMetadata={resultsMetadata}
              columns={columns}
              data={data}
              style={{ marginTop: 16 }}
            />
          </Panel>
        </Collapse>
      </SPQLCollapse>
    </div>
  );
}

function SPQLUIAnd(props) {
  const additionalStyles = props.style ?? {};
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 54,
        height: 32,
        background: '#164C7E',
        borderRadius: 4,
        ...additionalStyles,
      }}
    >
      <span>AND</span>
    </div>
  );
}

function SPQLUIFilter({
  filterRef,
  filterCount,
  changeSPQLQuery,
  setFilters,
  idx,
  filter,
  filterableFields,
  isLast,
  ...props
}) {
  const DeleteIcon = (props) => <Icon component={delete_icon} {...props} />;

  const [field, setField] = useState(filter?.field ?? '');
  const [selectedOperator, setSelectedOperator] = useState(
    filter?.operator ?? '',
  );
  const [filterValue, setFilterValue] = useState(filter?.value ?? '');
  const spqlSorting = useSelector(getSPQLSorting);
  const history = useHistory();
  const location = useLocation();
  const spqlQueryValidationMessage = useSelector(getSPQLQueryValidationMessage);

  const spqlFieldsMetadata = useSelector(getSPQLFieldsMetadata);
  let filterOptions = [];
  if (spqlFieldsMetadata) {
    filterOptions = Object.keys(spqlFieldsMetadata)
      .map((field) => {
        return {
          label: field,
          value: field,
        };
      })
      .sort((a, b) => (a.value > b.value ? 1 : -1));
  }

  let operators = [];
  const keyExists = Object.keys(spqlFieldsMetadata).indexOf(field) >= 0;
  if (field && keyExists) {
    operators = spqlFieldsMetadata[field].valid_operators;
    operators = operators.map((operator) => {
      return {
        label: operator,
        value: operator,
      };
    });
  }
  const updateURL = (query, sorting) => {
    const params = new URLSearchParams(location.search);
    const queryUUID = params.get('query-uuid');
    if (queryUUID) {
      return;
    }

    history.push(
      `/web-scanner?query=${encodeURIComponent(
        query,
      )}&sorting=${sorting.map((item) => encodeURIComponent(item)).join(',')}`,
    );
  };

  const isEverythingFilled = () => {
    return field && selectedOperator && filterValue?.trim()?.length > 0;
  };

  const updateFilter = (field, operator, value) => {
    if (field && operator && value) {
      setFilters((prevFilters) => {
        const newFilters = [...prevFilters];
        newFilters[idx] = {
          field,
          operator,
          value,
        };
        filterRef.current = newFilters;
        return newFilters;
      });
    }
  };

  return (
    <>
      <div style={props.style} className={styles['spql-filter-container']}>
        {!(isLast && idx === 0 && filterCount === 1) && (
          <div style={{ position: 'absolute', top: '4px', right: '8px' }}>
            <Button
              data-cy={`spqlDeleteFilter-${idx}`}
              icon={<DeleteIcon style={{ fontSize: 10 }} />}
              ghost
              onClick={() => {
                setFilters((prevFilters) => {
                  const newFilters = [...prevFilters];
                  newFilters.splice(idx, 1);
                  filterRef.current = newFilters;
                  return newFilters;
                });
              }}
              style={{
                color: 'white',
                border: 0,
                width: 24,
                height: 24,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            />
          </div>
        )}
        <div style={{ textAlign: 'left', padding: 8 }}>
          <div>
            <span>Field name</span>
          </div>
          <div style={{ marginTop: 2 }}>
            <Select
              data-cy={`spqlFilterField-${idx}`}
              value={field}
              showSearch
              onChange={(val) => {
                if (val !== field) {
                  setSelectedOperator('');
                }
                setField(val);
                updateFilter(val, selectedOperator, filterValue);
              }}
              options={filterOptions}
              style={{ width: '100%' }}
            />
          </div>
        </div>
        {spqlQueryValidationMessage && !isEverythingFilled() && (
          <div style={{ textAlign: 'left', padding: '0 8px', marginTop: -8 }}>
            <span style={{ color: 'red' }}>
              Please select a parameter, operator and value
            </span>
          </div>
        )}
        <div style={{ textAlign: 'left', padding: 8, display: 'flex' }}>
          <div style={{ width: 88, marginRight: 8 }}>
            <div>Operator</div>
            <div>
              <Select
                data-cy={`spqlFilterOperator-${idx}`}
                value={selectedOperator}
                onChange={(val) => {
                  setSelectedOperator(val);
                  updateFilter(field, val, filterValue);
                }}
                options={operators}
                style={{ width: '100%' }}
              />
            </div>
          </div>
          <div style={{ flexGrow: 1 }}>
            <div>Value</div>
            <div>
              <Input
                data-cy={`spqlFilterValue-${idx}`}
                defaultValue={filterValue}
                onBlur={(e) => {
                  const newValue = e.target.value;
                  updateFilter(field, selectedOperator, newValue);
                }}
                style={{ width: '100%' }}
              />
            </div>
          </div>
        </div>
      </div>
      {!isLast && <SPQLUIAnd style={{ marginLeft: 8, marginRight: 8 }} />}
    </>
  );
}

export function SPQLFilterSection({
  filterRef,
  changeSPQLQuery,
  queryFilters,
  filterableFields,
  filters,
  setFilters,
  ...props
}) {
  return (
    <div
      style={{
        padding: '16px 8px 16px 16px',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
      }}
    >
      {filters.map((filter, index) => (
        <SPQLUIFilter
          filterRef={filterRef}
          changeSPQLQuery={changeSPQLQuery}
          filterCount={filters.length}
          setFilters={setFilters}
          idx={index}
          filter={filter}
          isLast={index === filters.length - 1}
          filterableFields={filterableFields}
          key={`${filter.field}-${filter.operator}-${filter.value}-${index}`}
        />
      ))}
      <Button
        data-cy="spqlAddNewFilter"
        onClick={() => {
          setFilters((prevFilters) => {
            const newFilters = [
              ...prevFilters,
              {
                field: '',
                operator: '',
                value: '',
              },
            ];
            return newFilters;
          });
        }}
        style={{ marginLeft: 8, border: 0 }}
      >
        Add New
      </Button>
    </div>
  );
}

function getSortingPositionOptions(sortingCount) {
  let options = [];
  for (let i = 1; i <= sortingCount; i++) {
    options.push({
      label: i.toString(),
      value: i,
    });
  }
  return options;
}

function SPQLUISorting({
  setSorting,
  changeSPQLSorting,
  sortingCount,
  idx,
  sort,
  isLast,
  sortableFields,
  ...props
}) {
  const DeleteIcon = (props) => <Icon component={delete_icon} {...props} />;
  let sortOptions = getSortOptions(sortableFields);
  let sortingPositionOptions = getSortingPositionOptions(sortingCount);
  const spqlQuery = useSelector(getSPQLQuery);
  const history = useHistory();
  const location = useLocation();
  const [sortOption, setSortOption] = useState(sort ?? '');
  const [sortingPosition, setSortingPosition] = useState(idx + 1);

  const isSavedQuery = location.search?.includes('query-uuid');

  const updateSorting = (sortOption, newIdx) => {
    if (sortOption && newIdx) {
      setSorting((prevSorting) => {
        const newSorting = [...prevSorting];
        if (newIdx > newSorting.length) {
          newSorting.push(sortOption);
        } else {
          const temp = newSorting[newIdx - 1];
          newSorting[newIdx - 1] = sortOption;
          newSorting[prevSorting.indexOf(sortOption)] = temp;
        }
        changeSPQLSorting(newSorting);
        if (!isSavedQuery) {
          history.push(
            `/web-scanner?query=${encodeURIComponent(
              spqlQuery,
            )}&sorting=${newSorting
              .map((item) => encodeURIComponent(item))
              .join(',')}`,
          );
        }
        return newSorting;
      });
    }
  };
  const deleteSorting = (sortOption) => {
    setSorting((prevSorting) => {
      const newSorting = [...prevSorting];
      newSorting.splice(prevSorting.indexOf(sortOption), 1);
      changeSPQLSorting(newSorting);
      if (!isSavedQuery) {
        history.push(
          `/web-scanner?query=${encodeURIComponent(
            spqlQuery,
          )}&sorting=${newSorting
            .map((item) => encodeURIComponent(item))
            .join(',')}`,
        );
      }
      return newSorting;
    });
  };

  return (
    <div style={props.style} className={styles['spql-sorting-container']}>
      {!(isLast && idx === 0 && sortingCount === 1) && (
        <div style={{ position: 'absolute', top: '0', right: '3px' }}>
          <Button
            data-cy={`spqlDeleteSorting-${idx}`}
            icon={<DeleteIcon style={{ fontSize: 10 }} />}
            ghost
            onClick={() => {
              deleteSorting(sortOption);
            }}
            style={{
              color: 'white',
              border: 0,
              width: 24,
              height: 24,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          />
        </div>
      )}

      <div style={{ textAlign: 'left', padding: 8, display: 'flex' }}>
        <div style={{ flexGrow: 1, marginRight: 8 }}>
          <div>Data</div>
          <div>
            <Select
              showSearch
              data-cy={`spqlSorting-${idx}`}
              value={sortOption}
              onChange={(val) => {
                setSortOption(val);
                updateSorting(val, sortingPosition);
              }}
              options={sortOptions}
              style={{ width: '100%' }}
            />
          </div>
        </div>
        <div style={{ width: 64 }}>
          <div>Order</div>
          <div>
            <Select
              data-cy={`spqlSortingPosition-${idx}`}
              onChange={(val) => {
                setSortingPosition(val);
                updateSorting(sortOption, val);
              }}
              value={idx + 1}
              options={sortingPositionOptions}
              style={{ width: '100%' }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export function SPQLSortingSection({
  changeSPQLSorting,
  sortableFields,
  ...props
}) {
  const spqlSorting = useSelector(getSPQLSorting);
  const [sorting, setSorting] = useState(spqlSorting);

  useEffect(() => {
    if (spqlSorting?.length === 0) {
      setSorting(['']);
    } else {
      setSorting(spqlSorting);
    }
  }, [spqlSorting]);

  return (
    <div style={{ padding: '0 8px 16px 16px' }}>
      <div style={{ textAlign: 'left' }}>Sort order</div>
      <div
        style={{
          marginTop: 4,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        {sorting?.map((sort, index) => (
          <SPQLUISorting
            setSorting={setSorting}
            changeSPQLSorting={changeSPQLSorting}
            style={index !== sorting.length - 1 ? { marginRight: 70 } : {}}
            sortingCount={sorting.length}
            idx={index}
            sort={sort}
            isLast={index === sorting.length - 1}
            sortableFields={sortableFields}
            key={`${sort}-${index}`}
          />
        ))}
        {sorting.length < MAX_SORTING_FIELDS && (
          <Button
            data-cy={`spqlAddNewSorting`}
            onClick={() => {
              setSorting((prevSorting) => {
                const newSorting = [...prevSorting, ''];
                return newSorting;
              });
            }}
            style={{ marginTop: 8, marginLeft: 8, border: 0 }}
          >
            Add New
          </Button>
        )}
      </div>
    </div>
  );
}

function tokenize(query) {
  let tokens = [];
  let token = '';
  let inQuotes = false;
  let inArray = false;
  let arrayToken = '';

  for (let char of query) {
    if (inArray) {
      if (char === ']') {
        inArray = false;
        tokens.push('[' + arrayToken + ']');
        arrayToken = '';
      } else {
        arrayToken += char;
      }
      continue;
    }

    if (char === '"' && inQuotes) {
      inQuotes = false;
      tokens.push(token);
      token = '';
    } else if (char === '"' && !inQuotes) {
      inQuotes = true;
    } else if (char === '[' && !inQuotes) {
      inArray = true;
    } else if (char === ' ' && !inQuotes && !inArray) {
      if (token) {
        tokens.push(token);
        token = '';
      }
    } else {
      token += char;
    }
  }
  if (token) tokens.push(token);
  if (arrayToken) tokens.push('[' + arrayToken + ']');

  return tokens;
}

function parse(tokens) {
  let ast = [];
  let current = {};

  for (let token of tokens) {
    if (token?.toUpperCase() === 'AND') {
      ast.push(current);
      current = {};
    } else if (!current.field) {
      current.field = token;
    } else if (!current.operator) {
      current.operator = token;
    } else {
      current.value = token;
    }
  }
  ast.push(current);
  return ast;
}

function reconstructQuery(ast) {
  let query = '';

  for (let i = 0; i < ast.length; i++) {
    let node = ast[i];
    if (i > 0) {
      query += ' AND ';
    }
    query += node.field + ' ' + node.operator + ' ';

    if (node.value?.startsWith('[') && node.value?.endsWith(']')) {
      const nodeValues = node.value.slice(1, -1).split(', ');
      let arrayValues = nodeValues.map((val) => {
        return isNaN(val) && !val?.includes('"') ? '"' + val + '"' : val;
      });
      query += '[' + arrayValues.join(', ') + ']';
    } else {
      query += isNaN(node.value) ? '"' + node.value + '"' : node.value;
    }
  }

  return query;
}

function spqlFiltersToRawQuery(filters) {
  if (!filters) return '';
  if (filters.length === 0) return '';

  return reconstructQuery(filters);
}

function isSPQLQueryValid(query, validKeywords) {
  if (!query || query.trim().length === 0) return false;
  const tokens = tokenize(query);
  const ast = parse(tokens);

  const keywords = Object.keys(validKeywords);
  for (let node of ast) {
    if (!keywords.includes(node.field)) {
      return false;
    }
  }
  return true;
}

function spqlRawQueryToFilters(query, validKeywords) {
  if (!query) return [{ field: '', operator: '', value: '' }];
  const tokens = tokenize(query);
  const ast = parse(tokens);

  const keywords = Object.keys(validKeywords);
  return ast.filter((node) => keywords.includes(node.field));
}

export function SPQLUIConstructor({
  filterRef,
  changeSPQLQuery,
  changeSPQLSorting,
  filterableFields,
  sortableFields,
  getDeepScan,
  spqlSorting,
  ...props
}) {
  const spqlQuery = useSelector(getSPQLQuery);
  const spqlFieldsMetadata = useSelector(getSPQLFieldsMetadata);
  const [filters, setFilters] = useState([]);
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const savedQueries = useSelector(getSPQLSavedQueries);

  const urlParams = new URLSearchParams(location.search);
  const queryUUID = urlParams.get('query-uuid');

  useEffect(() => {
    const filterQuery = spqlRawQueryToFilters(spqlQuery, spqlFieldsMetadata);
    setFilters(filterQuery);
    filterRef.current = filterQuery;
  }, [spqlQuery, spqlFieldsMetadata]);

  const handleReset = () => {
    dispatch(changeSPQLQueryValidationMessage(''));
    if (queryUUID) {
      const savedQuery = savedQueries.find((query) => query.uuid === queryUUID);
      if (savedQuery) {
        changeSPQLQuery(savedQuery.query);
        changeSPQLSorting(['scan_date/desc']);
        return;
      }
    }
    changeSPQLQuery('');
    changeSPQLSorting(['scan_date/desc']);
  };
  const updateURL = (query, sorting) => {
    const params = new URLSearchParams(location.search);
    const queryUUID = params.get('query-uuid');
    if (queryUUID) {
      return;
    }

    history.push(
      `/web-scanner?query=${encodeURIComponent(
        query,
      )}&sorting=${sorting.map((item) => encodeURIComponent(item)).join(',')}`,
    );
  };
  const handleSearch = () => {
    const query = spqlFiltersToRawQuery(filters);
    if (!isSPQLQueryValid(query, spqlFieldsMetadata)) {
      if (query.trim().length === 0) {
        dispatch(
          changeSPQLQueryValidationMessage('Please enter a valid query'),
        );
        return;
      }
      dispatch(
        changeSPQLQueryValidationMessage(
          'The Field Key does not exist, or has been entered incorrectly.',
        ),
      );
      return;
    }
    updateURL(query, spqlSorting);
    getDeepScan({
      query,
      sort: spqlSorting,
      limit: 100,
      skip: 0,
    });
  };

  return (
    <div>
      <SPQLFilterSection
        filterRef={filterRef}
        filters={filters}
        setFilters={setFilters}
        changeSPQLQuery={changeSPQLQuery}
        filterableFields={filterableFields}
      />
      <SPQLSortingSection
        changeSPQLSorting={changeSPQLSorting}
        sortableFields={sortableFields}
      />
      <div style={{ textAlign: 'left', padding: '8px 16px 8px 16px' }}>
        <Button
          onClick={handleSearch}
          style={{ background: '#177DDC', color: 'white' }}
        >
          Search
        </Button>

        <Tooltip
          title={
            !!queryUUID
              ? 'Reset the query to the previous saved state'
              : 'Reset the query to the empty state'
          }
        >
          <Button
            onClick={handleReset}
            ghost
            style={{ border: 0, marginLeft: 8 }}
          >
            Reset
          </Button>
        </Tooltip>
      </div>
    </div>
  );
}

const SharedIcon = (props) => <Icon component={shared_icon} {...props} />;
const OptionLabel = memo(function OptionLabel({ label, isShared, ...props }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {isShared && <Icon type={UserOutlined} />}
      {!isShared && <SharedIcon />}
      <span style={{ marginLeft: 8, color: 'white' }}>{label}</span>
    </div>
  );
});

function SelectExistingSPQLQuery({
  getSavedSPQLQueries,
  changeSPQLSorting,
  changeSPQLQuery,
  ...props
}) {
  const [form] = Form.useForm();
  const DownIcon = (props) => <Icon component={down_icon} {...props} />;
  const [modalVisible, setModalVisible] = useState(false);
  const history = useHistory();

  const savedSPQLQueries = useSelector(getSPQLSavedQueries);

  let selectOptions = [];
  if (savedSPQLQueries?.length > 0) {
    selectOptions = savedSPQLQueries.map((query) => ({
      label: (
        <OptionLabel
          label={query.name}
          isShared={query.scope === 'ORGANISATION'}
        />
      ),
      value: JSON.stringify(query),
      key: query.id,
    }));
  }

  useEffect(() => {
    getSavedSPQLQueries();
  }, []);

  return (
    <>
      {modalVisible && (
        <Modal
          open={modalVisible}
          title="Choose a saved search"
          onCancel={() => setModalVisible(false)}
          footer={[
            <Button
              type="link"
              key="cancel"
              onClick={() => setModalVisible(false)}
            >
              Cancel
            </Button>,
            <Button
              type="primary"
              htmlType="submit"
              key="submit"
              onClick={() => form.submit()}
            >
              Apply
            </Button>,
          ]}
        >
          <Form
            preserve={false}
            onFinish={(val) => {
              const query = JSON.parse(val.spqlSearch);
              const sorting = query.sorting.split(',');
              changeSPQLQuery(query.query);
              changeSPQLSorting(sorting);
              setModalVisible(false);
              history.push(`/web-scanner?query-uuid=${query.uuid}`);
            }}
            layout="vertical"
            form={form}
          >
            <Form.Item name="spqlSearch" label="Query">
              <Select placeholder="Select query" options={selectOptions} />
            </Form.Item>
          </Form>
        </Modal>
      )}
      <Button onClick={() => setModalVisible(true)}>
        Saved
        <DownIcon />
      </Button>
    </>
  );
}

function SaveSPQLQueryModal({
  modalVisible,
  setModalVisible,
  handleFormSubmit,
  form,
  isUpdatable,
  ...props
}) {
  return (
    <Modal
      title="Save Search"
      open={modalVisible}
      destroyOnClose={true}
      onCancel={() => setModalVisible(false)}
      footer={[
        <Button type="link" key="cancel" onClick={() => setModalVisible(false)}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          htmlType="submit"
          onClick={() => form.submit()}
        >
          {isUpdatable() ? 'Update' : 'Save'}
        </Button>,
      ]}
    >
      <Form layout="vertical" form={form} onFinish={handleFormSubmit}>
        <Form.Item name="uuid" hidden={true}>
          <Input />
        </Form.Item>
        <Form.Item
          rules={[
            {
              required: true,
              message: 'Please enter search name',
            },
            {
              max: 40,
              message: "The search name can't be longer than 40 characters",
            },
            {
              pattern: new RegExp(/^[0-9A-Za-z/.:/_\*-\s\[\]]+$/),
              message: 'Special characters are not allowed.',
            },
          ]}
          label="Search name"
          name="name"
        >
          <Input maxLength={40} placeholder="Enter search name" />
        </Form.Item>
        <Form.Item
          rules={[
            {
              max: 3000,
              message: "The description can't be longer than 3000 characters",
            },
          ]}
          label="Description"
          name="description"
        >
          <TextArea maxLength={3000} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

function SaveSPQLQuery({ updateSPQLQuery, createSPQLQuery, ...props }) {
  const [form] = useForm();
  const [modalVisible, setModalVisible] = useState(false);
  const currentSPQLQuery = useSelector(getSPQLQuery);
  const currentSPQLSorting = useSelector(getSPQLSorting);
  const createSPQLQueryStatus = useSelector(getSPQLCreateQueryStatus);
  const savedSPQLQueries = useSelector(getSPQLSavedQueries);
  const fieldsMetadata = useSelector(getSPQLFieldsMetadata);
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();

  const params = new URLSearchParams(location.search);
  const queryUUID = params.get('query-uuid');
  const isUpdatable = () => !!queryUUID;

  const savedQuery = savedSPQLQueries.find((query) => query.uuid === queryUUID);

  const handleFormSubmit = (formValues) => {
    if (isUpdatable()) {
      updateSPQLQuery({
        ...formValues,
        scope: savedQuery.scope,
        query: currentSPQLQuery,
        sorting: currentSPQLSorting,
      });
      return;
    }
    createSPQLQuery({
      ...formValues,
      query: currentSPQLQuery,
      sorting: currentSPQLSorting,
    });
  };

  useEffect(() => {
    if (createSPQLQueryStatus === 'success') {
      let savedQuery = savedSPQLQueries?.reduce((max, query) => {
        return max.updated > query.updated ? max : query;
      }, {});
      if (savedQuery) {
        history.push(`/web-scanner?query-uuid=${savedQuery.uuid}`);
      }
      setModalVisible(false);
    }
  }, [createSPQLQueryStatus, savedSPQLQueries]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const queryUUID = params.get('query-uuid');
    const shouldPrefillForm = !!queryUUID;
    if (!shouldPrefillForm) {
      form.setFieldsValue({
        uuid: null,
        name: '',
        description: '',
      });
      return;
    }

    const savedQuery = savedSPQLQueries.find(
      (query) => query.uuid === queryUUID,
    );
    if (savedQuery) {
      form.setFieldsValue({
        uuid: savedQuery.uuid,
        name: savedQuery.name,
        description: savedQuery.description,
      });
    }
  }, [
    location.search,
    form,
    savedSPQLQueries,
    currentSPQLQuery,
    currentSPQLSorting,
  ]);

  return (
    <>
      {modalVisible && (
        <SaveSPQLQueryModal
          modalVisible={modalVisible}
          setModalVisible={setModalVisible}
          handleFormSubmit={handleFormSubmit}
          form={form}
          isUpdatable={isUpdatable}
        />
      )}
      <Button
        disabled={
          isUpdatable()
            ? (currentSPQLSorting.join(',') === savedQuery?.sorting &&
                currentSPQLQuery === savedQuery?.query) ||
              !savedQuery?.is_mine
            : false
        }
        onClick={() => {
          if (!isSPQLQueryValid(currentSPQLQuery, fieldsMetadata)) {
            if (currentSPQLQuery.trim().length === 0) {
              dispatch(
                changeSPQLQueryValidationMessage('Please enter a valid query'),
              );
              return;
            }
            dispatch(
              changeSPQLQueryValidationMessage(
                'The Field Key does not exist, or has been entered incorrectly.',
              ),
            );
            return;
          }
          if (isUpdatable()) {
            // just update the query
            updateSPQLQuery({
              ...savedQuery,
              query: currentSPQLQuery,
              sorting: currentSPQLSorting,
            });
            return;
          }
          setModalVisible(true);
        }}
        type="primary"
      >
        {isUpdatable() ? 'Update' : 'Save'}
      </Button>
    </>
  );
}

function SaveAsSPQLQuery({
  spqlQuery,
  modalVisible,
  setModalVisible,
  createSPQLQuery,
  ...props
}) {
  const [form] = useForm();
  const currentQuery = useSelector(getSPQLQuery);
  const dispatch = useDispatch();
  const currentSorting = useSelector(getSPQLSorting);
  const createSPQLQueryStatus = useSelector(getSPQLCreateQueryStatus);
  const savedSPQLQueries = useSelector(getSPQLSavedQueries);
  const location = useLocation();
  const history = useHistory();
  const handleFormSubmit = (formValues) => {
    createSPQLQuery({
      ...formValues,
      scope: 'USER',
      query: currentQuery,
      sorting: currentSorting,
    });
  };

  function handleCloseModal() {
    setModalVisible(false);
    dispatch(setCreateSPQLQueryStatus(STATUS.IDLE));
    dispatch(changeSPQLQueryValidationMessage(''));
  }

  useEffect(() => {
    if (spqlQuery) {
      form.setFieldsValue({
        uuid: null,
        name: spqlQuery.name,
        description: spqlQuery.description,
      });
    }
  }, [spqlQuery, form]);

  useEffect(() => {
    if (createSPQLQueryStatus === 'success') {
      let savedQuery = savedSPQLQueries?.reduce((max, query) => {
        return max.updated > query.updated ? max : query;
      }, {});
      if (savedQuery) {
        history.push(`/web-scanner?query-uuid=${savedQuery.uuid}`);
        handleCloseModal();
      }
    }
  }, [createSPQLQueryStatus, savedSPQLQueries, history, setModalVisible]);

  return (
    <Modal
      title="Save As Search"
      open={modalVisible}
      onCancel={() => setModalVisible(false)}
      footer={[
        <Button type="link" key="cancel" onClick={() => setModalVisible(false)}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          htmlType="submit"
          onClick={() => form.submit()}
        >
          Save
        </Button>,
      ]}
    >
      <Form layout="vertical" form={form} onFinish={handleFormSubmit}>
        <Form.Item name="uuid" hidden={true}>
          <Input />
        </Form.Item>
        <Form.Item
          rules={[
            {
              required: true,
              message: 'Please enter search name',
            },
            {
              max: 40,
              message: "The search name can't be longer than 40 characters",
            },
            {
              pattern: new RegExp(/^[0-9A-Za-z/.:/_\*-\s\[\]]+$/),
              message: 'Special characters are not allowed.',
            },
          ]}
          label="Search name"
          name="name"
        >
          <Input maxLength={40} placeholder="Enter search name" />
        </Form.Item>
        <Form.Item
          rules={[
            {
              max: 3000,
              message: "The description can't be longer than 3000 characters",
            },
          ]}
          label="Description"
          name="description"
        >
          <TextArea maxLength={3000} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

function UpdateSPQLMetdataModal({
  updateSPQLQuery,
  modalVisible,
  setModalVisible,
  ...props
}) {
  const [form] = useForm();
  const dispatch = useDispatch();
  const createSPQLQueryStatus = useSelector(getSPQLCreateQueryStatus);
  const savedSPQLQueries = useSelector(getSPQLSavedQueries);
  const location = useLocation();
  const history = useHistory();

  const params = new URLSearchParams(location.search);
  const queryUUID = params.get('query-uuid');

  const savedQuery = savedSPQLQueries.find((query) => query.uuid === queryUUID);

  const handleFormSubmit = (formValues) => {
    updateSPQLQuery({
      ...savedQuery,
      ...formValues,
      sorting: savedQuery?.sorting?.split(','),
    });
  };

  function handleCloseModal() {
    setModalVisible(false);
    dispatch(setCreateSPQLQueryStatus(STATUS.IDLE));
    dispatch(changeSPQLQueryValidationMessage(''));
  }

  useEffect(() => {
    if (createSPQLQueryStatus === 'success') {
      let savedQuery = savedSPQLQueries?.reduce((max, query) => {
        return max.updated > query.updated ? max : query;
      }, {});
      if (savedQuery) {
        history.push(`/web-scanner?query-uuid=${savedQuery.uuid}`);
        handleCloseModal();
      }
    }
  }, [createSPQLQueryStatus, savedSPQLQueries]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const queryUUID = params.get('query-uuid');
    const shouldPrefillForm = !!queryUUID;
    if (!shouldPrefillForm) {
      form.setFieldsValue({
        uuid: null,
        name: '',
        description: '',
      });
      return;
    }

    const savedQuery = savedSPQLQueries.find(
      (query) => query.uuid === queryUUID,
    );
    if (savedQuery) {
      form.setFieldsValue({
        uuid: savedQuery.uuid,
        name: savedQuery.name,
        description: savedQuery.description,
      });
    }
  }, [location.search, form, savedSPQLQueries]);

  return (
    <Modal
      title="Edit Search Metadata"
      open={modalVisible}
      onCancel={() => setModalVisible(false)}
      footer={[
        <Button type="link" key="cancel" onClick={() => setModalVisible(false)}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          htmlType="submit"
          onClick={() => form.submit()}
        >
          Update
        </Button>,
      ]}
    >
      <Form layout="vertical" form={form} onFinish={handleFormSubmit}>
        <Form.Item name="uuid" hidden={true}>
          <Input />
        </Form.Item>
        <Form.Item
          rules={[
            {
              required: true,
              message: 'Please enter search name',
            },
            {
              max: 40,
              message: "The search name can't be longer than 40 characters",
            },
            {
              pattern: new RegExp(/^[0-9A-Za-z/.:/_\*-\s\[\]]+$/),
              message: 'Special characters are not allowed.',
            },
          ]}
          label="Search name"
          name="name"
        >
          <Input maxLength={40} placeholder="Enter search name" />
        </Form.Item>
        <Form.Item
          rules={[
            {
              max: 3000,
              message: "The description can't be longer than 3000 characters",
            },
          ]}
          label="Description"
          name="description"
        >
          <TextArea maxLength={3000} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

function MoreOptionsMenu({
  createSPQLQuery,
  savedQuery,
  updateSPQLQuery,
  deleteSPQLQuery,
  ...props
}) {
  const history = useHistory();
  const dispatch = useDispatch();
  const [modalVisible, setModalVisible] = useState(false);
  const [updateModalVisible, setUpdateModalVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);

  const handleSaveAs = () => {
    setModalVisible(true);
  };

  const handleDeleteSPQLQuery = () => {
    if (!savedQuery) return;
    deleteSPQLQuery(savedQuery.uuid);
    dispatch(resetDeepScanToInitialState());
    history.push('/web-scanner');
  };

  const handleShareSPQLQuery = () => {
    if (!savedQuery) return;
    const scope = savedQuery.scope === 'ORGANISATION' ? 'USER' : 'ORGANISATION';
    updateSPQLQuery({
      ...savedQuery,
      sorting: savedQuery.sorting.split(','),
      scope,
    });
  };

  const handleUpdateSPQLQuery = () => {
    setUpdateModalVisible(true);
  };

  return (
    <>
      <Menu style={{ width: 200 }}>
        {savedQuery?.is_mine && (
          <Menu.Item
            onClick={handleUpdateSPQLQuery}
            disabled={!savedQuery?.is_mine}
            key="edit-metadata"
          >
            Edit Metadata
          </Menu.Item>
        )}
        <Menu.Item onClick={handleSaveAs} key="save-as">
          Save as
        </Menu.Item>
        {savedQuery?.is_mine && (
          <>
            <Menu.Item
              onClick={handleShareSPQLQuery}
              disabled={!savedQuery?.is_mine}
              key="share"
            >
              {savedQuery?.scope === 'ORGANISATION' ? 'Unshare' : 'Share'}
            </Menu.Item>
            <Menu.Item
              onClick={() => setDeleteModalVisible(true)}
              disabled={!savedQuery?.is_mine}
              key="delete"
            >
              <span style={{ color: '#E84749' }}>Delete</span>
            </Menu.Item>
          </>
        )}
      </Menu>
      {savedQuery && updateModalVisible && (
        <UpdateSPQLMetdataModal
          updateSPQLQuery={updateSPQLQuery}
          setModalVisible={setUpdateModalVisible}
          modalVisible={updateModalVisible}
        />
      )}
      {savedQuery && modalVisible && (
        <SaveAsSPQLQuery
          spqlQuery={savedQuery}
          modalVisible={modalVisible}
          setModalVisible={setModalVisible}
          createSPQLQuery={createSPQLQuery}
        />
      )}
      {savedQuery && deleteModalVisible && (
        <DeleteRecipeModal
          title={'Are you sure you want to delete this query?'}
          description={`You are about to delete your ${savedQuery?.name} query. This action cannot be undone.`}
          isDeleteModalVisible={deleteModalVisible}
          handleOk={() => {
            handleDeleteSPQLQuery();
            setDeleteModalVisible(false);
          }}
          handleCancel={() => {
            setDeleteModalVisible(false);
          }}
        />
      )}
    </>
  );
}

function MoreOptions({
  createSPQLQuery,
  updateSPQLQuery,
  deleteSPQLQuery,
  ...props
}) {
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const queryUUID = params.get('query-uuid');

  const savedQuery = useSelector(getSPQLSavedQueries).find(
    (query) => query.uuid === queryUUID,
  );

  return (
    <Dropdown
      disabled={!savedQuery}
      trigger={['click']}
      overlay={
        <MoreOptionsMenu
          createSPQLQuery={createSPQLQuery}
          updateSPQLQuery={updateSPQLQuery}
          deleteSPQLQuery={deleteSPQLQuery}
          savedQuery={savedQuery}
        />
      }
    >
      <Button
        icon={<MoreOutlined />}
        ghost
        style={{ marginLeft: 8, border: 0, color: 'white' }}
      />
    </Dropdown>
  );
}

function NewSPQLQueryButton({ ...props }) {
  const history = useHistory();
  const dispatch = useDispatch();
  const handleNewSPQLQueryClick = () => {
    dispatch(resetDeepScanToInitialState());
    history.push('/web-scanner');
  };
  return (
    <Button onClick={handleNewSPQLQueryClick} type="link">
      New
    </Button>
  );
}

const DescriptionIcon = (props) => (
  <Icon style={{ fontSize: '14px' }} component={description_icon} {...props} />
);

export function SPQL({
  sortableFields,
  filterableFields,
  fieldsMetadata,
  defaultDisplayFields,
  resultsMetadata,
  resultsData,
  resultsStatus,
  getDeepScanMetadata,
  getDeepScan,
  getRecentlyRanSPQLQueries,
  getQueryExamples,
  getSavedSPQLQueries,
  createSPQLQuery,
  updateSPQLQuery,
  deleteSPQLQuery,
  changeSPQLQuery,
  changeSPQLSorting,
  changeSPQLSelectedColumns,
  subscriptionPlanStatus,
  subscriptionPlan,
  ...props
}) {
  const location = useLocation();
  const dispatch = useDispatch();
  const savedSPQLQueries = useSelector(getSPQLSavedQueries);
  const filterRef = useRef(null);

  const spqlFieldsMetadata = useSelector(getSPQLFieldsMetadata);
  const spqlSorting = useSelector(getSPQLSorting);

  const createQuery = (params) => {
    createSPQLQuery(params);
    incrementUIRequestsCounter();
  };
  const updateQuery = (params) => {
    updateSPQLQuery(params);
    incrementUIRequestsCounter();
  };
  const deleteQuery = (params) => {
    deleteSPQLQuery(params);
    incrementUIRequestsCounter();
  };
  const fetchData = (params) => {
    getDeepScan(params);
    incrementUIRequestsCounter();
  };

  let description = `Web Scanner uses SPQL, a free-form query language that enables data exploration via the Silent Push API.`;
  const params = new URLSearchParams(location.search);
  const queryUUID = params.get('query-uuid');
  if (queryUUID) {
    const savedQuery = savedSPQLQueries.find(
      (query) => query.uuid === queryUUID,
    );
    if (savedQuery && savedQuery.description) {
      description = savedQuery.description;
    }
  }
  let descriptionWithEllipsis =
    description?.length > 200
      ? description?.slice(0, 200) + '...'
      : description;

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const queryUUID = params.get('query-uuid');
    const query = params.get('query');
    const sorting = params.get('sorting');
    if (queryUUID) {
      const savedQuery = savedSPQLQueries.find(
        (query) => query.uuid === queryUUID,
      );
      if (!savedQuery) {
        return;
      }
      changeSPQLQuery(savedQuery.query);
      const savedSorting = savedQuery.sorting.split(',');
      changeSPQLSorting(savedSorting);
      dispatch(changeTitle(savedQuery.name));
      dispatch(changeSPQLQueryValidationMessage(''));
      return;
    }
    if (query) {
      changeSPQLQuery(query);
    }
    if (sorting) {
      changeSPQLSorting(sorting.split(','));
    }
    dispatch(changeTitle('Web Scanner'));
  }, [location.search, savedSPQLQueries]);

  useEffect(() => {
    document.title = `Web Scanner - Silent Push`;
    getDeepScanMetadata();
    getRecentlyRanSPQLQueries();
    getQueryExamples();
    return () => {
      dispatch(resetDeepScanToInitialState());
    };
  }, []);

  const tabItems = [
    {
      key: 'spql-raw',
      label: <SPQLTabPaneLabel label={'Command Line Query'} />,
      children: (
        <SPQLRawQueryContainer
          changeSPQLSorting={changeSPQLSorting}
          changeSPQLQuery={changeSPQLQuery}
          getDeepScan={fetchData}
          sortableFields={sortableFields}
        />
      ),
    },
    {
      key: 'spql-ui-constructor',
      label: <SPQLTabPaneLabel label="Query Constructor" />,
      children: (
        <SPQLUIConstructor
          filterRef={filterRef}
          getDeepScan={fetchData}
          changeSPQLSorting={changeSPQLSorting}
          changeSPQLQuery={changeSPQLQuery}
          filterableFields={filterableFields}
          sortableFields={sortableFields}
          spqlSorting={spqlSorting}
        />
      ),
    },
  ];

  return subscriptionPlanStatus === STATUS.LOADED ? (
    subscriptionPlan?.has_access_to_web_scanner ? (
      <div>
        <div className={styles['spql-container-header']}>
          <SPQLBadge />
          <div className={styles['spql-header-vertical-line']} />
          <div
            className={styles['spql-header-text-container']}
            style={{ textAlign: 'left' }}
          >
            {descriptionWithEllipsis.length !== description.length ? (
              <>
                <span>{descriptionWithEllipsis}</span>
                <Tooltip title={description}>
                  <DescriptionIcon
                    style={{ cursor: 'pointer', margin: '0 4px', fontSize: 16 }}
                  />
                </Tooltip>
              </>
            ) : (
              <span>{description}</span>
            )}{' '}
            <a
              className={styles['spql-knowledge-base']}
              target="_blank"
              href="https://help.silentpush.com/docs/spql"
            >
              Knowledge base.
            </a>
          </div>
          <div
            style={{
              display: 'flex',
              flexGrow: 1,
              justifyContent: 'flex-end',
              alignItems: 'center',
            }}
          >
            <NewSPQLQueryButton />
            <div
              style={{ margin: '0 8px' }}
              className={styles['spql-header-vertical-line']}
            />
            <SelectExistingSPQLQuery
              changeSPQLQuery={changeSPQLQuery}
              changeSPQLSorting={changeSPQLSorting}
              getSavedSPQLQueries={getSavedSPQLQueries}
            />
            <div
              style={{ margin: '0 8px' }}
              className={styles['spql-header-vertical-line']}
            />
            <SaveSPQLQuery
              updateSPQLQuery={updateQuery}
              createSPQLQuery={createQuery}
            />
            <MoreOptions
              createSPQLQuery={createQuery}
              updateSPQLQuery={updateQuery}
              deleteSPQLQuery={deleteQuery}
            />
          </div>
        </div>
        <div style={{ background: '#1C3138' }}>
          <SPQLTabPane>
            <Tabs
              className={styles['spql-tabs']}
              items={tabItems}
              onTabClick={(key) => {
                if (key === 'spql-raw') {
                  if (filterRef.current != null) {
                    const spqlQueryFromFilters = spqlFiltersToRawQuery(
                      filterRef.current,
                    );
                    changeSPQLQuery(spqlQueryFromFilters);
                  }
                }
              }}
              defaultActiveKey="spql-raw"
            />
          </SPQLTabPane>
        </div>
        <SPQLSearchResults
          getDeepScan={fetchData}
          changeSPQLQuery={changeSPQLQuery}
          resultsMetadata={resultsMetadata}
          changeSPQLSelectedColumns={changeSPQLSelectedColumns}
          columns={defaultDisplayFields}
          data={resultsData}
        />
      </div>
    ) : (
      <PermissionDeniedPage />
    )
  ) : (
    <LoadingSilentPush size={80} positionCenter />
  );
}
